<script lang="ts">
  import DieElement from '../dice-tray/die.svelte';
  import { selectFaceData } from './modals';
  import Frame from '../components/frame.svelte';
  import Chrome from '../components/chrome.svelte';
  import type { Die } from '../models/die.type';
  import { activePlayer, lockedDie } from '../../store';
  import { isTechniquePossible, techniquesOnOffer } from '../models/techniques.type';
  import { get } from 'svelte/store';

  let onConfirm: Function | null = null;
  /** Index of the chosen face on the die, not the face itself. */
  let face: number | null = null;
  let faces: Array<Die> = $state.raw([]);
  /** Per face, X/Y: techniques possible with it showing, out of all on offer. */
  let tallies: Array<string> = $state.raw([]);

  /**
   * What choosing this face would lead to: the locked dice with this die
   * showing it, counted as the board counts them.
   */
  function tallyFor(original: Die, variant: Die) {
    const dice = [...lockedDie.filter((die) => die !== original), variant];
    const offered = techniquesOnOffer(dice, get(activePlayer));
    const possible = offered.filter((t) => isTechniquePossible([], dice, t)).length;
    return `${possible}/${offered.length}`;
  }

  selectFaceData.subscribe((value) => {
    if (value && value.callback) {
      onConfirm = value.callback;

      faces = Array.from({ length: 6 }, (_, i) => {
        const die = value.die.clone();
        die.activeFace = i;
        die.id = 'x' + i;
        die.selected = false;
        return die;
      });
      tallies = faces.map((variant) => tallyFor(value.die, variant));
    } else {
      onConfirm = null;
    }
  });

  const confirm = () => {
    if (onConfirm) {
      onConfirm(face);
    }
    selectFaceData.set(null);
  };

  function chooseDie(selectedDie: Die) {
    /*if(selectedDie.used){
      return;
    }*/
    face = selectedDie.activeFace;

    // New dice rather than flipping `selected` on the old ones: a die changed in
    // place is the same object, and the tray would have no reason to redraw it.
    faces = faces.map((die: Die) => {
      const next = die.clone();
      next.selected = die === selectedDie;
      return next;
    });
  }
</script>

{#if $selectFaceData}
  <div class="modal-container">
    <div class="modal">
      <Frame>
        <div class="header">
          <h1>{$selectFaceData.title ?? 'Select a Face'}</h1>
          {#if $selectFaceData.step}
            <p class="step">{$selectFaceData.step}</p>
          {/if}
        </div>

        <div class="body">
          <!-- A row of its own rather than a DiceTray, which places dice
               absolutely and leaves nowhere to put each face's count. -->
          <!-- Text rather than only a tooltip: a phone never hovers. -->
          <p class="explain">
            Under each face: how many of your techniques you could make with it showing, out of all
            your dice can make.
          </p>

          <div class="faces">
            {#each faces as option, index (option.id)}
              <button
                class="face-option {option.selected ? 'chosen' : ''}"
                onclick={() => chooseDie(option)}
                title="Techniques possible with this face showing, out of all on offer"
              >
                <DieElement die={option} die3D={false} hover={false} />
                <span class="tally">{tallies[index]}</span>
              </button>
            {/each}
          </div>
        </div>

        <div class="functions">
          <Chrome chroming="gold" edge="circle" thickness="thin" click={confirm}>
            <div class="button">Confirm</div>
          </Chrome>
        </div>
      </Frame>
    </div>
  </div>
{/if}

<style lang="less">
  .step {
    margin: 0.2em 0 0;
    opacity: 0.8;
    font-size: 0.9rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }

  .explain {
    margin: 0 0 12px;
    text-align: center;
    opacity: 0.9;
  }

  .faces {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 10px;
  }

  // Over the global button look, which would put a bevel round every face.
  .face-option {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    padding: 6px;
    background: none;
    box-shadow: none;
    border-radius: 12px;

    &.chosen {
      box-shadow:
        0 0 0 2px rgba(255, 255, 255, 0.85),
        0 0 12px rgba(255, 255, 255, 0.55);
    }

    .tally {
      color: white;
      text-shadow: 0 1px 3px black;
      font-variant-numeric: tabular-nums;
    }
  }
</style>
